import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  LinearProgress,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  Pagination
} from '@mui/material';
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles';
import {
  Upload as UploadIcon,
  Camera,
  FileText,
  Image,
  CheckCircle,
  AlertCircle,
  X,
  Search,
  Download,
  DollarSign,
  Send
} from 'lucide-react';

import ReceiptService from '../services/AGTUps-product-services/receiptService';
import TransactionService from '../services/AGTUps-product-services/transactionService';

/* ===== Paleta de colores y Theme para que coincida con otros shortcuts ===== */
const COLORS = {
  primary: 'rgba(128, 128, 128, 0.5)',
  text: {
    light: '#FFFFFF',
    dark: '#FFFFFF'
  }
};

const createCustomTheme = (mode, actualMode) =>
  createTheme({
    palette: {
      mode,
      actualMode
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none'
            }
          },
          contained: {
            backgroundColor: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.primary,
            color: actualMode === 'wallpaper' ? '#000000' : '#ffffff',
            '&:hover': {
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.85)' : COLORS.primary
            },
            '&:disabled': {
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.12)'
                  : 'rgba(0, 0, 0, 0.12)'
            }
          },
          text: {
            color: actualMode === 'wallpaper' ? '#FFFFFF' : COLORS.text[mode]
          },
          outlined: {
            borderRadius: '12px',
            borderColor: COLORS.primary,
            color: COLORS.primary,
            '&:hover': {
              borderColor: COLORS.primary,
              backgroundColor:
                mode === 'dark'
                  ? 'rgba(128, 128, 128, 0.08)'
                  : 'rgba(128, 128, 128, 0.04)'
            }
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: '16px',
            border: '1px solid',
            borderColor: actualMode === 'wallpaper'
              ? 'rgba(128, 128, 128, 0.3)'
              : (mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'),
            backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined,
            backdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
            WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(10px)' : 'none'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.25)' : undefined,
              backdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              WebkitBackdropFilter: actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
              '& fieldset': {
                borderColor: actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : undefined
              },
              '& input, & textarea': {
                color: actualMode === 'wallpaper' ? '#FFFFFF' : undefined
              }
            },
            '& .MuiInputLabel-root': {
              color: actualMode === 'wallpaper' ? 'rgba(255, 255, 255, 0.7)' : undefined
            }
          }
        }
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '12px'
          }
        }
      }
    },
    typography: {
      h5: {
        fontWeight: 600
      },
      button: {
        textTransform: 'none'
      }
    }
  });

// =========== Dialogs Auxiliares =============
function CameraDialog({ open, onClose, onCapture }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (open) startCamera();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line
  }, [open]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error starting camera:', error);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
  };

  const handleCapture = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `receipt_${Date.now()}.jpg`, {
            type: 'image/jpeg'
          });
          onCapture(file);
        }
      },
      'image/jpeg',
      1
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Scan Receipt
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 2 }}>
          <video
            ref={videoRef}
            autoPlay
            style={{ width: '100%', borderRadius: '12px', background: '#000' }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              border: '2px dashed rgba(128, 128, 128, 0.5)',
              borderRadius: '12px',
              pointerEvents: 'none'
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<Camera size={20} />}
          onClick={handleCapture}
        >
          Capture
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PreviewDialog({ open, onClose, uploadData, onConfirm, confirming }) {
  if (!uploadData) return null;
  const { receipt, transactionData } = uploadData;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{receipt?.fileName || 'Preview'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Se ha procesado el recibo y estos son los datos extraídos:
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            backgroundColor: '#f9f9f9',
            p: 2,
            borderRadius: 1
          }}
        >
          {JSON.stringify(transactionData, null, 2)}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={
            confirming ? <CircularProgress size={20} /> : <Send size={20} />
          }
          onClick={onConfirm}
          disabled={confirming}
        >
          {confirming ? 'Saving...' : 'Confirm & Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ==================================
// Componente Principal del Shortcut
// ==================================
function QuickUploadShortcut({ open, onClose }) {
  // En lugar de forzar la detección en window.matchMedia,
  // tomamos el modo del theme "padre":
  const parentTheme = useTheme();
  const customTheme = createCustomTheme(parentTheme.palette.mode, parentTheme.palette.actualMode);

  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [uploadInPreview, setUploadInPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  // Lista de recibos (o transacciones) recientes
  const [recentUploads, setRecentUploads] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Para el snackbar de éxito
  const [showSnackbar, setShowSnackbar] = useState(false);

  useEffect(() => {
    if (open) {
      fetchRecentUploads(currentPage, searchTerm);
    } else {
      resetStates();
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchRecentUploads(currentPage, searchTerm);
    }
    // eslint-disable-next-line
  }, [currentPage, searchTerm]);

  const resetStates = () => {
    setFiles([]);
    setCameraDialogOpen(false);
    setPreviewDialogOpen(false);
    setUploadInPreview(null);
    setConfirming(false);
  };

  const fetchRecentUploads = async (page, term) => {
    try {
      const res = await ReceiptService.getRecentUploads(page, 10, term);
      if (res?.data) {
        setRecentUploads(res.data.receipts);
        setTotalPages(res.data.totalPages);
      }
    } catch (error) {
      console.error('Error fetching recent uploads:', error);
    }
  };

  // Función para manejar la elección de archivos
  const handleSelectFiles = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length) {
      processFiles(selected);
    }
  };

  // Función para manejar la subida
  const processFiles = async (selectedFiles) => {
    for (const file of selectedFiles) {
      const newFileObj = {
        file,
        id: Date.now() + Math.random(),
        status: 'uploading'
      };
      setFiles((prev) => [...prev, newFileObj]);

      try {
        // Llamada a tu backend para subir + procesar
        const uploadResponse = await ReceiptService.uploadReceipt(file);
        if (uploadResponse?.data?.transactionData) {
          newFileObj.status = 'complete';
          newFileObj.processedData = uploadResponse.data; // { receipt, transactionData }
          setUploadInPreview(uploadResponse.data);
          setPreviewDialogOpen(true);

          // Agregamos el recibo a la lista de "recentUploads"
          setRecentUploads((prev) => [uploadResponse.data.receipt, ...prev]);
          setShowSnackbar(true);
        }
      } catch (err) {
        console.error('Error uploading file:', err);
        newFileObj.status = 'error';
      } finally {
        setFiles((prev) => {
          return prev.map((f) => (f.id === newFileObj.id ? newFileObj : f));
        });
      }
    }
  };

  // Función para confirmar la transacción
  const handleConfirmTransaction = async () => {
    if (!uploadInPreview?.transactionData) return;
    setConfirming(true);
    try {
      await TransactionService.confirmTransaction(uploadInPreview.transactionData);
      setPreviewDialogOpen(false);
      setUploadInPreview(null);
    } catch (err) {
      console.error('Error confirming transaction:', err);
    } finally {
      setConfirming(false);
    }
  };

  // Se abre la cámara
  const handleOpenCamera = () => {
    setCameraDialogOpen(true);
  };

  const handleCaptureImage = (file) => {
    setCameraDialogOpen(false);
    // Subir y procesar la imagen capturada
    processFiles([file]);
  };

  // Render del cuerpo principal en el Dialog
  const renderDialogContent = () => (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Sección de Upload */}
      <Box>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          sx={{ textTransform: 'none', mb: 2 }}
          onClick={() => fileInputRef.current?.click()}
        >
          Select File(s)
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.pdf"
          style={{ display: 'none' }}
          multiple
          onChange={handleSelectFiles}
        />
        <Button
          variant="outlined"
          startIcon={<Camera />}
          sx={{ textTransform: 'none', ml: 2 }}
          onClick={handleOpenCamera}
        >
          Camera
        </Button>
      </Box>

      {/* Lista de archivos en proceso */}
      {files.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Files in Process
          </Typography>
          {files.map((f) => (
            <Box
              key={f.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1,
                borderRadius: 1,
                mb: 1,
                backgroundColor:
                  f.status === 'error' ? 'error.light' : 'transparent'
              }}
            >
              {f.file.type.startsWith('image/') ? (
                <Image size={24} />
              ) : (
                <FileText size={24} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" noWrap>
                  {f.file.name}
                </Typography>
                {f.status === 'uploading' && <LinearProgress sx={{ mt: 1 }} />}
                {f.status === 'complete' && (
                  <Chip
                    icon={<CheckCircle size={16} />}
                    label="Processed"
                    color="success"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
                {f.status === 'error' && (
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}
                  >
                    <AlertCircle size={20} color="red" />
                    <Typography variant="caption" color="error">
                      Upload failed
                    </Typography>
                  </Box>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() =>
                  setFiles((prev) => prev.filter((x) => x.id !== f.id))
                }
              >
                <X size={16} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Recent Uploads */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Recent Uploads
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search receipts..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={16} />
              </InputAdornment>
            )
          }}
          sx={{ mb: 2 }}
        />
        {recentUploads.length > 0 ? (
          <Box sx={{ maxHeight: 180, overflow: 'auto' }}>
            {recentUploads.map((item) => (
              <Box
                key={item.receiptId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2
                }}
              >
                {item.fileType === 'image' ? (
                  <Image size={20} />
                ) : (
                  <FileText size={20} />
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2">{item.fileName}</Typography>
                  {item.status === 'processed' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DollarSign size={14} style={{ marginRight: 4 }} />
                      <Typography variant="caption" color="success.main">
                        ${item.amount}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    {item.uploadDate}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => window.open(item.fileUrl, '_blank')}
                >
                  <Download size={16} />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No receipts found.
          </Typography>
        )}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
              color="primary"
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={customTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="quick-upload-dialog"
      >
        <DialogTitle id="quick-upload-dialog">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Quick Receipt Upload
          </Typography>
        </DialogTitle>
        <DialogContent>{renderDialogContent()}</DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>

        {/* Camera Dialog */}
        <CameraDialog
          open={cameraDialogOpen}
          onClose={() => setCameraDialogOpen(false)}
          onCapture={handleCaptureImage}
        />

        {/* Preview Dialog */}
        <PreviewDialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          uploadData={uploadInPreview}
          onConfirm={handleConfirmTransaction}
          confirming={confirming}
        />

        {/* Snackbar de éxito */}
        <Snackbar
          open={showSnackbar}
          autoHideDuration={4000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity="success"
            sx={{ borderRadius: '12px' }}
          >
            Receipt processed successfully!
          </Alert>
        </Snackbar>
      </Dialog>
    </ThemeProvider>
  );
}

export default QuickUploadShortcut;
